import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  Payout,
  PayoutDownload,
  PayoutEntry,
  PayoutList,
  PayoutOverview,
  RecordAdvanceInput,
  RecordPayoutAdjustmentInput,
  SendStatementInput,
  SettlePayoutInput,
  StaffLedger,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  getMyEarnings,
  getPayout,
  getPayoutOverview,
  getPayouts,
  getStaffLedger,
  getStatementDownload,
  recordAdjustment,
  recordAdvance,
  sendStatement,
  settlePayout,
  type PeriodParams,
} from './payouts-api.js';

/**
 * The wage screens' data layer.
 *
 * NOTHING HERE GOES THROUGH THE OFFLINE WRITE QUEUE, and that is the most important line in this
 * file. A payment to a customer is taken at a counter and must survive a dead connection; a
 * settlement hands over cash AND stamps a fortnight of work as paid. Replaying one silently
 * twenty minutes later — against jobs that may have been settled from another phone in between —
 * is a far worse failure than "you are offline, try again". The server's own
 * `expectedNetPaise` check exists for the same reason.
 *
 * Every write invalidates the whole module, because almost all of them move more than one
 * screen: settling changes the person's ledger, the shop's outstanding total and the payout
 * list at once.
 */

export const payoutKeys = {
  all: ['payouts'] as const,
  overview: (params: PeriodParams) => ['payouts', 'overview', params] as const,
  ledger: (staffUserId: string, params: PeriodParams) =>
    ['payouts', 'ledger', staffUserId, params] as const,
  me: (params: PeriodParams) => ['payouts', 'me', params] as const,
  list: (params: PeriodParams & { staffUserId?: string }) => ['payouts', 'list', params] as const,
  detail: (payoutId: string) => ['payouts', 'detail', payoutId] as const,
};

function useInvalidatePayouts(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: payoutKeys.all });
    // A settlement stamps assignments and posts an expense, so both of those screens are stale.
    void queryClient.invalidateQueries({ queryKey: ['assignments'] });
    void queryClient.invalidateQueries({ queryKey: ['expenses'] });
  };
}

export function usePayoutOverview(
  params: PeriodParams = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<PayoutOverview> {
  const client = useApiClient();
  return useQuery({
    queryKey: payoutKeys.overview(params),
    queryFn: () => getPayoutOverview(client, params),
    placeholderData: (previous) => previous,
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

export function useStaffLedger(
  staffUserId: string | null,
  params: PeriodParams = {},
): UseQueryResult<StaffLedger> {
  const client = useApiClient();
  return useQuery({
    queryKey: payoutKeys.ledger(staffUserId ?? '', params),
    queryFn: () => getStaffLedger(client, staffUserId as string, params),
    enabled: staffUserId !== null,
  });
}

/** The karigar's own earnings. Available to every role — a small shop's owner stitches too. */
export function useMyEarnings(params: PeriodParams = {}): UseQueryResult<StaffLedger> {
  const client = useApiClient();
  return useQuery({
    queryKey: payoutKeys.me(params),
    queryFn: () => getMyEarnings(client, params),
  });
}

export function usePayouts(
  params: PeriodParams & { staffUserId?: string } = {},
): UseQueryResult<PayoutList> {
  const client = useApiClient();
  return useQuery({
    queryKey: payoutKeys.list(params),
    queryFn: () => getPayouts(client, params),
  });
}

export function usePayout(payoutId: string | null): UseQueryResult<Payout> {
  const client = useApiClient();
  return useQuery({
    queryKey: payoutKeys.detail(payoutId ?? ''),
    queryFn: () => getPayout(client, payoutId as string),
    enabled: payoutId !== null,
  });
}

export function useSettlePayout(
  staffUserId: string,
): UseMutationResult<Payout, unknown, SettlePayoutInput> {
  const client = useApiClient();
  const invalidate = useInvalidatePayouts();
  return useMutation({
    mutationFn: (input: SettlePayoutInput) => settlePayout(client, staffUserId, input),
    onSuccess: invalidate,
  });
}

export function useRecordAdvance(
  staffUserId: string,
): UseMutationResult<PayoutEntry, unknown, RecordAdvanceInput> {
  const client = useApiClient();
  const invalidate = useInvalidatePayouts();
  return useMutation({
    mutationFn: (input: RecordAdvanceInput) => recordAdvance(client, staffUserId, input),
    onSuccess: invalidate,
  });
}

export function useRecordAdjustment(
  staffUserId: string,
): UseMutationResult<PayoutEntry, unknown, RecordPayoutAdjustmentInput> {
  const client = useApiClient();
  const invalidate = useInvalidatePayouts();
  return useMutation({
    mutationFn: (input: RecordPayoutAdjustmentInput) =>
      recordAdjustment(client, staffUserId, input),
    onSuccess: invalidate,
  });
}

/**
 * The statement PDF's link, fetched on demand rather than with the page.
 *
 * A mutation and not a query, deliberately: asking for it makes the server RENDER the document
 * if it is missing, and a link that expires in fifteen minutes should be minted when somebody
 * asks for it rather than every time a screen mounts.
 */
export function useStatementDownload(): UseMutationResult<PayoutDownload, unknown, string> {
  const client = useApiClient();
  return useMutation({
    mutationFn: (payoutId: string) => getStatementDownload(client, payoutId),
  });
}

export function useSendStatement(): UseMutationResult<
  Payout,
  unknown,
  { payoutId: string; input?: SendStatementInput }
> {
  const client = useApiClient();
  const invalidate = useInvalidatePayouts();
  return useMutation({
    mutationFn: ({ payoutId, input }: { payoutId: string; input?: SendStatementInput }) =>
      sendStatement(client, payoutId, input ?? {}),
    onSuccess: invalidate,
  });
}
